import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';
import { environment } from '../../../environments/environment';
import { ProcessLog } from 'src/app/user-identification/models/user-identification';
import { CloudLogsService, LogType } from './cloud-logs.service';
import { MediaServerRestartService } from './media-server-restart.service';

/**
 * `iceConnectionState`/`connectionState` = `disconnected` suele ser transitorio.
 * Solo republish si permanece así más allá de esta gracia (evita cortes a ~3s).
 */
const DISCONNECTED_REPUBLISH_GRACE_MS = 12_000;
/** Intervalo de muestreo para comprobar bytes RTP salientes (gate post-WHIP). */
const OUTBOUND_MEDIA_POLL_MS = 400;
/** Tiempo máximo por defecto para detectar incremento de bytesSent en vídeo. */
const OUTBOUND_MEDIA_DEFAULT_TIMEOUT_MS = 3_000;

export enum WebRTCStatus {
	DISCONNECTED = 'disconnected',
	CONNECTING = 'connecting',
	CONNECTED = 'connected',
	STREAMING = 'streaming',
	ERROR = 'error',
	FAILED = 'failed',
	REPUBLISHING = 'republishing',
}

export interface IWebRTCConfig {
	iceServers: RTCIceServer[];
	enableAudio?: boolean;
	enableVideo?: boolean;
	bandwidth?: number;
	debug?: boolean;
	offerToReceiveAudio?: boolean;
	offerToReceiveVideo?: boolean;
	autoRepublishEnabled?: boolean;
	autoRepublishIntervalMs?: number;
	candidateTypes?: string[];
}

/**
 * Publicación WebRTC hacia SRS vía WHIP (HTTP SDP).
 * Sustituye el señalizado WebSocket de Ant Media.
 */
@Injectable({
	providedIn: 'root',
})
export class WebRTCStreamingService {
	private peerConnection: RTCPeerConnection | null = null;
	private localStream: MediaStream | null = null;
	private statusSubject = new BehaviorSubject<WebRTCStatus>(WebRTCStatus.DISCONNECTED);
	private localStreamSubject = new BehaviorSubject<MediaStream | null>(null);
	private destroy$ = new Subject<void>();

	private reconnectAttempts = 0;
	private currentDeviceId: string | null = null;
	private currentConfig: IWebRTCConfig | null = null;
	private currentStreamId: string | null = null;

	private backupConfig: IWebRTCConfig | null = null;
	private backupDeviceId: string | null = null;
	private backupStreamId: string | null = null;

	private autoRepublishIntervalJob: any = null;
	private isPublishing = false;
	private publishStartTime: number = 0;
	private isRepublishing = false;
	/** Timestamp del primer `disconnected` sostenido; null si no aplica. */
	private disconnectedSince: number | null = null;
	private statsInterval = null;

	private videoTrackSender: RTCRtpSender | null = null;
	private audioTrackSender: RTCRtpSender | null = null;
	private isStartingPublish = false;
	private isInitializing = false;
	private pendingInitPromise: Promise<boolean> | null = null;

	/** URL de sesión WHIP (cabecera Location) para DELETE al parar */
	private whipResourceUrl: string | null = null;
	/** Stream a aplicar con replaceTrack cuando la PC pase a connected (race cámara vs WHIP). */
	private pendingReplaceStream: MediaStream | null = null;

	private defaultConfig: IWebRTCConfig = {
		iceServers: [{ urls: 'stun:stun1.l.google.com:19302' }],
		enableAudio: true,
		enableVideo: true,
		bandwidth: 900,
		debug: false,
		offerToReceiveAudio: false,
		offerToReceiveVideo: false,
		autoRepublishEnabled: true,
		autoRepublishIntervalMs: 3000,
		// En WSL/SRS local el media UDP falla; SRS está en protocol=tcp.
		candidateTypes: ['tcp'],
	};

	private cloudLogsService = inject(CloudLogsService);
	private mediaServerRestartService = inject(MediaServerRestartService);

	public async initializeConnection(
		stream: MediaStream,
		deviceId: string,
		config?: Partial<IWebRTCConfig>,
		streamId?: string,
	): Promise<boolean> {
		if (this.isInitializing && this.pendingInitPromise) return this.pendingInitPromise;

		if (
			this.peerConnection &&
			this.peerConnection.connectionState !== 'closed' &&
			this.peerConnection.connectionState !== 'failed' &&
			this.currentStreamId === (streamId || deviceId)
		) {
			return true;
		}

		this.isInitializing = true;
		this.pendingInitPromise = this._doInitialize(stream, deviceId, config, streamId);

		try {
			return await this.pendingInitPromise;
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ streaming: `Error during initialization: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			throw error;
		} finally {
			this.isInitializing = false;
			this.pendingInitPromise = null;
		}
	}

	private async _doInitialize(
		stream: MediaStream,
		deviceId: string,
		config?: Partial<IWebRTCConfig>,
		streamId?: string,
	): Promise<boolean> {
		try {
			if (!stream || !stream.active) throw new Error('Stream is null or not active');

			if (!(this.localStream && this.localStream.active && this.localStream === stream)) {
				this.cloudLogsService.sendCloudLogs(
					{
						webrtc: 'initializeConnection: full disconnect — new stream reference or inactive previous stream',
						sameStreamReference: this.localStream === stream,
						previousStreamActive: this.localStream?.active,
						incomingStreamActive: stream?.active,
					},
					ProcessLog.IN_PROCESS,
					LogType.Info,
				);
				this.disconnect();
				await new Promise((resolve) => setTimeout(resolve, 100));
			}

			this.statusSubject.next(WebRTCStatus.CONNECTING);
			this.localStream = stream;
			this.localStreamSubject.next(stream);
			this.currentDeviceId = deviceId;
			this.currentStreamId = streamId || deviceId;
			this.currentConfig = { ...this.defaultConfig, ...config };

			this.backupConfig = { ...this.currentConfig };
			this.backupDeviceId = deviceId;
			this.backupStreamId = streamId || deviceId;

			await this.initializePeerConnection();

			if (this.currentConfig.autoRepublishEnabled) this.startAutoRepublishMonitoring();

			await this.startPublishing();

			this.reconnectAttempts = 0;
			return true;
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `Initialization error: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			this.statusSubject.next(WebRTCStatus.ERROR);
			this.mediaServerRestartService.notifyMediaServerFailure(
				`webrtc_init_failed:${error?.message ?? error?.toString() ?? 'unknown'}`,
			);
			return false;
		}
	}

	private async initializePeerConnection(): Promise<void> {
		if (!this.currentConfig) throw new Error('Configuration not set');

		this.peerConnection = new RTCPeerConnection({
			iceServers: this.currentConfig.iceServers,
		});

		this.videoTrackSender = null;
		this.audioTrackSender = null;

		this.setupPeerConnectionEvents();
		this.addStreamTracks(this.localStream!, this.currentConfig);
	}

	/**
	 * Publica a SRS con WHIP: offer SDP → POST → answer SDP.
	 */
	private async startPublishing(): Promise<void> {
		if (this.isStartingPublish) return;
		this.isStartingPublish = true;

		try {
			if (!this.peerConnection || !this.currentStreamId) {
				throw new Error('PeerConnection or streamId not initialized');
			}

			this.isPublishing = false;
			this.publishStartTime = Date.now();
			this.statusSubject.next(WebRTCStatus.STREAMING);

			const offer = await this.peerConnection.createOffer({
				offerToReceiveAudio: this.currentConfig?.offerToReceiveAudio ?? false,
				offerToReceiveVideo: this.currentConfig?.offerToReceiveVideo ?? false,
			});
			await this.peerConnection.setLocalDescription(offer);
			await this.waitForIceGatheringComplete();

			const localSdp = this.peerConnection.localDescription?.sdp;
			if (!localSdp) throw new Error('Missing local SDP after ICE gathering');

			const whipUrl = this.buildWhipUrl(this.currentStreamId);
			const response = await fetch(whipUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/sdp' },
				body: localSdp,
			});

			if (!response.ok) {
				const body = await response.text().catch(() => '');
				throw new Error(`WHIP publish failed: HTTP ${response.status} ${body}`);
			}

			const location = response.headers.get('Location');
			this.whipResourceUrl = location ? this.resolveUrl(location, whipUrl) : whipUrl;

			const answerSdp = await response.text();
			const pc = this.peerConnection;
			if (!pc) {
				throw new Error('PeerConnection lost before WHIP answer could be applied');
			}
			await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answerSdp }));

			this.isPublishing = true;
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `WHIP publish OK stream=${this.sanitizeStreamId(this.currentStreamId)}` },
				ProcessLog.IN_PROCESS,
				LogType.Info,
			);
		} catch (error: any) {
			this.isPublishing = false;
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `Error in startPublishing: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			throw new Error(`Failed to start publishing: ${error?.toString()}`);
		} finally {
			this.isStartingPublish = false;
		}
	}

	private buildWhipUrl(streamId: string): string {
		const base = environment.srsWhipUrl.endsWith('/')
			? environment.srsWhipUrl
			: `${environment.srsWhipUrl}/`;
		const params = new URLSearchParams({
			app: environment.srsApp || 'live',
			stream: this.sanitizeStreamId(streamId),
		});
		const token = (environment as { srsWhipToken?: string }).srsWhipToken;
		if (token) {
			params.set('token', token);
		}
		return `${base}?${params.toString()}`;
	}

	/** SRS acepta mejor ids alfanuméricos / guiones / guiones bajos */
	private sanitizeStreamId(streamId: string): string {
		const cleaned = streamId.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
		return cleaned.slice(0, 128) || `stream_${Date.now()}`;
	}

	private resolveUrl(location: string, requestUrl: string): string {
		const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
		const requestAbsolute = requestUrl.startsWith('http') ? requestUrl : `${baseOrigin}${requestUrl}`;
		try {
			const resolved = new URL(location, requestAbsolute);
			// Con proxy local (/rtc → :1985), DELETE debe ir al mismo origen del front
			if (environment.srsWhipUrl.startsWith('/')) {
				return `${baseOrigin}${resolved.pathname}${resolved.search}`;
			}
			return resolved.toString();
		} catch {
			return location.startsWith('/') ? `${baseOrigin}${location}` : location;
		}
	}

	private waitForIceGatheringComplete(timeoutMs = 5000): Promise<void> {
		if (!this.peerConnection) return Promise.resolve();
		if (this.peerConnection.iceGatheringState === 'complete') return Promise.resolve();

		return new Promise((resolve) => {
			const pc = this.peerConnection!;
			const timer = setTimeout(() => {
				pc.removeEventListener('icegatheringstatechange', onChange);
				resolve();
			}, timeoutMs);

			const onChange = () => {
				if (pc.iceGatheringState === 'complete') {
					clearTimeout(timer);
					pc.removeEventListener('icegatheringstatechange', onChange);
					resolve();
				}
			};
			pc.addEventListener('icegatheringstatechange', onChange);
		});
	}

	private async deleteWhipSession(): Promise<void> {
		if (!this.whipResourceUrl) return;
		const url = this.whipResourceUrl;
		this.whipResourceUrl = null;
		try {
			await fetch(url, { method: 'DELETE' });
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `WHIP DELETE failed: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Warning,
			);
		}
	}

	private startAutoRepublishMonitoring(): void {
		if (!this.currentConfig?.autoRepublishEnabled) return;

		const intervalMs = this.currentConfig.autoRepublishIntervalMs || 3000;
		this.autoRepublishIntervalJob = setInterval(() => {
			this.checkAndRepublishIfRequired();
		}, intervalMs);

		this.cloudLogsService.sendCloudLogs(
			{ webrtc: `Auto-republish monitoring started with ${intervalMs}ms interval` },
			ProcessLog.IN_PROCESS,
			LogType.Info,
		);
	}

	private checkAndRepublishIfRequired(): void {
		if (this.isRepublishing || this.isStartingPublish || this.isInitializing) return;
		if (!this.peerConnection || !this.currentStreamId) return;

		const iceState = this.peerConnection.iceConnectionState;
		const connectionState = this.peerConnection.connectionState;
		const signalingState = this.peerConnection.signalingState;

		const isFailed =
			iceState === 'failed' || connectionState === 'failed' || signalingState === 'closed';
		const isDisconnected =
			!isFailed && (iceState === 'disconnected' || connectionState === 'disconnected');

		if (isDisconnected) {
			if (this.disconnectedSince == null) {
				this.disconnectedSince = Date.now();
			}
		} else {
			this.disconnectedSince = null;
		}

		const disconnectedTooLong =
			this.disconnectedSince != null &&
			Date.now() - this.disconnectedSince >= DISCONNECTED_REPUBLISH_GRACE_MS;

		// Solo si nunca llegó a publicar y no estamos en gracia de `disconnected`.
		const stuckStarting =
			!this.isPublishing &&
			this.publishStartTime > 0 &&
			Date.now() - this.publishStartTime > 8000 &&
			!isDisconnected &&
			!isFailed;

		const needsRepublish = stuckStarting || isFailed || disconnectedTooLong;

		if (needsRepublish) {
			this.statusSubject.next(WebRTCStatus.REPUBLISHING);
			this.cloudLogsService.sendCloudLogs(
				{
					webrtc: `Auto-republish triggered. ICE: ${iceState}, Connection: ${connectionState}${
						disconnectedTooLong ? ` (disconnected>${DISCONNECTED_REPUBLISH_GRACE_MS}ms)` : ''
					}`,
				},
				ProcessLog.IN_PROCESS,
				LogType.Warning,
			);
			this.performRepublish();
		}
	}

	private async performRepublish(): Promise<void> {
		this.isRepublishing = true;
		this.disconnectedSince = null;

		try {
			const preservedConfig = this.currentConfig || this.backupConfig;
			const preservedDeviceId = this.currentDeviceId || this.backupDeviceId;
			const preservedStreamId = this.currentStreamId || this.backupStreamId;
			const preservedLocalStream = this.localStream;

			if (!preservedConfig || !preservedDeviceId || !preservedLocalStream) {
				this.statusSubject.next(WebRTCStatus.ERROR);
				this.mediaServerRestartService.notifyMediaServerFailure('webrtc_republish_missing_state');
				return;
			}

			if (this.autoRepublishIntervalJob) {
				clearInterval(this.autoRepublishIntervalJob);
				this.autoRepublishIntervalJob = null;
			}

			await this.deleteWhipSession();
			this.cleanupPeerConnection();

			this.currentConfig = { ...preservedConfig };
			this.currentDeviceId = preservedDeviceId;
			this.currentStreamId = preservedStreamId;
			this.localStream = preservedLocalStream;
			this.backupConfig = { ...preservedConfig };
			this.backupDeviceId = preservedDeviceId;
			this.backupStreamId = preservedStreamId;

			await new Promise((resolve) => setTimeout(resolve, 500));
			await this.reinitializeConnection();

			if (this.currentConfig.autoRepublishEnabled) {
				this.startAutoRepublishMonitoring();
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `Republish failed: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			this.statusSubject.next(WebRTCStatus.ERROR);
			this.mediaServerRestartService.notifyMediaServerFailure(
				`webrtc_republish_failed:${error?.message ?? error?.toString() ?? 'unknown'}`,
			);
		} finally {
			this.isRepublishing = false;
		}
	}

	private setupPeerConnectionEvents(): void {
		if (!this.peerConnection) return;

		// Con WHIP los ICE van embebidos en el SDP; no hace falta trickle por señalización.
		this.peerConnection.onicecandidate = () => {};

		this.peerConnection.onconnectionstatechange = () => {
			const state = this.peerConnection?.connectionState;
			switch (state) {
				case 'connected':
					this.disconnectedSince = null;
					this.statusSubject.next(WebRTCStatus.CONNECTED);
					this.isPublishing = true;
					void this.applyPendingStreamReplace();
					if (this.currentConfig?.bandwidth) {
						this.changeBandwidth(this.currentConfig.bandwidth).catch((e) => console.error(e));
					}
					break;
				case 'connecting':
					this.statusSubject.next(WebRTCStatus.CONNECTING);
					break;
				case 'disconnected':
					if (this.disconnectedSince == null) {
						this.disconnectedSince = Date.now();
					}
					this.statusSubject.next(WebRTCStatus.DISCONNECTED);
					this.isPublishing = false;
					break;
				case 'failed':
					this.disconnectedSince = null;
					this.statusSubject.next(WebRTCStatus.FAILED);
					this.isPublishing = false;
					break;
				case 'closed':
					this.disconnectedSince = null;
					this.statusSubject.next(WebRTCStatus.DISCONNECTED);
					this.isPublishing = false;
					break;
			}
		};

		this.peerConnection.oniceconnectionstatechange = () => {
			const state = this.peerConnection?.iceConnectionState;
			if (state === 'connected' || state === 'completed') {
				this.disconnectedSince = null;
				return;
			}
			if (state === 'disconnected') {
				if (this.disconnectedSince == null) {
					this.disconnectedSince = Date.now();
				}
				this.isPublishing = false;
				this.cloudLogsService.sendCloudLogs(
					{ webrtc: `ICE connection state changed: ${state}` },
					ProcessLog.IN_PROCESS,
					LogType.Info,
				);
				return;
			}
			if (state === 'failed') {
				this.disconnectedSince = null;
				this.isPublishing = false;
				this.cloudLogsService.sendCloudLogs(
					{ webrtc: `ICE connection state changed: ${state}` },
					ProcessLog.IN_PROCESS,
					LogType.Info,
				);
			}
		};
	}

	private cleanupPeerConnection(): void {
		if (this.peerConnection) {
			this.peerConnection.close();
			this.peerConnection = null;
		}
	}

	private async reinitializeConnection(): Promise<void> {
		const configToUse = this.currentConfig || this.backupConfig;
		const deviceIdToUse = this.currentDeviceId || this.backupDeviceId;
		const streamIdToUse = this.currentStreamId || this.backupStreamId;

		if (!configToUse || !deviceIdToUse || !this.localStream) {
			throw new Error('Missing required data for reconnection even in backup');
		}

		if (!this.currentConfig) {
			this.currentConfig = { ...configToUse };
			this.currentDeviceId = deviceIdToUse;
			this.currentStreamId = streamIdToUse;
		}

		this.statusSubject.next(WebRTCStatus.CONNECTING);
		this.cleanupPeerConnection();
		await this.initializePeerConnection();

		if (this.currentConfig.autoRepublishEnabled) {
			this.startAutoRepublishMonitoring();
		}

		await this.startPublishing();
	}

	private addStreamTracks(stream: MediaStream, config: IWebRTCConfig): void {
		if (!this.peerConnection) return;

		stream.getTracks().forEach((track) => {
			if (track.kind === 'video' && config.enableVideo) {
				this.videoTrackSender = this.peerConnection!.addTrack(track, stream);
				// Evita ramp 180p→360p que rompe DVR MP4 (Mp4AvccChange).
				void this.lockVideoEncoding(config.bandwidth);
			} else if (track.kind === 'audio' && config.enableAudio) {
				this.audioTrackSender = this.peerConnection!.addTrack(track, stream);
			}
		});
	}

	/** Fija bitrate y evita que Chrome cambie resolución a mitad de sesión. */
	private async lockVideoEncoding(bandwidth?: number): Promise<void> {
		const videoSender = this.getVideoSender();
		if (!videoSender) return;

		const parameters = videoSender.getParameters();
		if (!parameters.encodings?.length) parameters.encodings = [{}];

		parameters.degradationPreference = 'maintain-resolution';
		parameters.encodings[0].scaleResolutionDownBy = 1;
		if (bandwidth && bandwidth > 0) {
			parameters.encodings[0].maxBitrate = bandwidth * 1000;
		}

		try {
			await videoSender.setParameters(parameters);
		} catch (error) {
			console.warn('lockVideoEncoding failed', error);
		}
	}

	public async changeBandwidth(bandwidth: number): Promise<void> {
		if (!this.peerConnection) throw new Error('PeerConnection not initialized');

		const videoSender = this.getVideoSender();
		if (!videoSender) throw new Error('Video sender not found to change bandwidth');

		const parameters = videoSender.getParameters();
		if (!parameters.encodings) parameters.encodings = [{}];

		parameters.degradationPreference = 'maintain-resolution';
		parameters.encodings[0].scaleResolutionDownBy = 1;

		if (bandwidth === 0) {
			delete parameters.encodings[0].maxBitrate;
		} else {
			parameters.encodings[0].maxBitrate = bandwidth * 1000;
		}

		await videoSender.setParameters(parameters);
	}

	private getVideoSender(): RTCRtpSender | null {
		if (!this.peerConnection) return null;
		for (const sender of this.peerConnection.getSenders()) {
			if (sender.track?.kind === 'video') return sender;
		}
		return null;
	}

	public getStatus(): Observable<WebRTCStatus> {
		return this.statusSubject.asObservable();
	}

	public getCurrentStatus(): WebRTCStatus {
		return this.statusSubject.value;
	}

	public isConnected(): boolean {
		return (
			this.statusSubject.value === WebRTCStatus.CONNECTED || this.statusSubject.value === WebRTCStatus.STREAMING
		);
	}

	private updateLocalVideoStream(stream: MediaStream): void {
		if (!this.localStream) return;
		const newVideoTrack = stream.getVideoTracks()[0];
		const oldVideoTrack = this.localStream.getVideoTracks()[0];
		if (oldVideoTrack) this.localStream.removeTrack(oldVideoTrack);
		if (newVideoTrack) this.localStream.addTrack(newVideoTrack);
	}

	private updateLocalAudioStream(stream: MediaStream): void {
		if (!this.localStream) return;
		const newAudioTrack = stream.getAudioTracks()[0];
		const oldAudioTrack = this.localStream.getAudioTracks()[0];
		if (oldAudioTrack) this.localStream.removeTrack(oldAudioTrack);
		if (newAudioTrack) this.localStream.addTrack(newAudioTrack);
	}

	public async updateStream(newStream: MediaStream): Promise<boolean> {
		if (!this.peerConnection) return false;

		if (!this.isConnected()) {
			this.pendingReplaceStream = newStream;
			return true;
		}

		return this.applyStreamToSenders(newStream);
	}

	private async applyPendingStreamReplace(): Promise<void> {
		const pending = this.pendingReplaceStream;
		if (!pending || !this.peerConnection || !this.isConnected()) return;

		this.pendingReplaceStream = null;
		const ok = await this.applyStreamToSenders(pending);
		if (!ok) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: 'Pending stream replace failed after CONNECTED' },
				ProcessLog.IN_PROCESS,
				LogType.Warning,
			);
		}
	}

	private async applyStreamToSenders(newStream: MediaStream): Promise<boolean> {
		if (!this.peerConnection) return false;

		try {
			for (const sender of this.peerConnection.getSenders()) {
				if (sender.track) {
					const newTrack = newStream.getTracks().find((track) => track.kind === sender.track?.kind);
					if (newTrack) {
						await sender.replaceTrack(newTrack);
						if (newTrack.kind === 'video') this.updateLocalVideoStream(newStream);
						else if (newTrack.kind === 'audio') this.updateLocalAudioStream(newStream);
					}
				}
			}

			this.localStream = newStream;
			this.localStreamSubject.next(newStream);

			if (this.backupConfig && this.currentDeviceId) {
				this.backupDeviceId = this.currentDeviceId;
				this.backupStreamId = this.currentStreamId;
			}

			return true;
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `Stream update error: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return false;
		}
	}

	public partialDisconnect(): void {
		if (this.autoRepublishIntervalJob) {
			clearInterval(this.autoRepublishIntervalJob);
			this.autoRepublishIntervalJob = null;
		}

		this.isPublishing = false;
		this.destroy$.next();
		void this.deleteWhipSession();

		if (this.peerConnection) {
			try {
				this.peerConnection.getSenders().forEach((sender) => {
					try {
						this.peerConnection?.removeTrack(sender);
					} catch (e) {
						this.cloudLogsService.sendCloudLogs(
							{ webrtc: `Error removing sender track: ${e?.toString()}` },
							ProcessLog.IN_PROCESS,
							LogType.Error,
						);
					}
				});
			} catch (error) {
				this.cloudLogsService.sendCloudLogs(
					{ webrtc: `Error removing senders during partial disconnect: ${error?.toString()}` },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
			}
		}

		this.videoTrackSender = null;
		this.audioTrackSender = null;
		this.destroy$ = new Subject<void>();

		if (this.peerConnection) {
			this.peerConnection.close();
			this.peerConnection = null;
		}
	}

	/**
	 * Cierra PC/WHIP sin matar tracks de cámara.
	 * Usado en RETRY MEDIA_SERVER para forzar republicado con el mismo getUserMedia.
	 */
	public resetPeerConnectionForRepublish(): void {
		if (this.autoRepublishIntervalJob) {
			clearInterval(this.autoRepublishIntervalJob);
			this.autoRepublishIntervalJob = null;
		}

		this.disconnectedSince = null;
		this.isPublishing = false;
		this.isStartingPublish = false;
		this.isInitializing = false;
		this.pendingInitPromise = null;
		this.pendingReplaceStream = null;
		this.destroy$.next();
		this.destroy$ = new Subject<void>();
		void this.deleteWhipSession();
		this.cleanupPeerConnection();
		this.videoTrackSender = null;
		this.audioTrackSender = null;
		// Invalidar streamId para que initializeConnection no haga early-return.
		this.currentStreamId = null;
		this.statusSubject.next(WebRTCStatus.DISCONNECTED);
	}

	public disconnect(): void {
		if (this.autoRepublishIntervalJob) {
			clearInterval(this.autoRepublishIntervalJob);
			this.autoRepublishIntervalJob = null;
		}

		this.disconnectedSince = null;
		this.isPublishing = false;
		this.isStartingPublish = false;
		this.pendingReplaceStream = null;
		this.destroy$.next();
		void this.deleteWhipSession();
		this.cleanupPeerConnection();

		if (!this.isRepublishing) {
			if (this.localStream) {
				this.localStream.getTracks().forEach((track) => track.stop());
				this.localStream = null;
				this.localStreamSubject.next(null);
			}
		}

		this.videoTrackSender = null;
		this.audioTrackSender = null;

		if (!this.isRepublishing) {
			this.currentStreamId = null;
			this.currentDeviceId = null;
			this.currentConfig = null;
			this.backupConfig = null;
			this.backupDeviceId = null;
			this.backupStreamId = null;
			this.isInitializing = false;
			this.pendingInitPromise = null;
			this.destroy$ = new Subject<void>();
		}

		this.statusSubject.next(WebRTCStatus.DISCONNECTED);
		this.cloudLogsService.sendCloudLogs(
			{ webrtc: 'WebRTC service completely disconnected (SRS WHIP)' },
			ProcessLog.FINISH,
			LogType.Info,
		);
	}

	public emergencyDisconnect(): void {
		try {
			void this.deleteWhipSession();

			if (this.peerConnection) {
				this.peerConnection.close();
				this.peerConnection = null;
			}

			if (this.autoRepublishIntervalJob) {
				clearInterval(this.autoRepublishIntervalJob);
				this.autoRepublishIntervalJob = null;
			}

			if (this.statsInterval) {
				clearInterval(this.statsInterval);
				this.statsInterval = null;
			}

			this.statusSubject.next(WebRTCStatus.DISCONNECTED);
			this.isPublishing = false;

			if (this.localStream) {
				this.localStream.getTracks().forEach((track) => track.stop());
				this.localStream = null;
				this.localStreamSubject.next(null);
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `Emergency disconnect error: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	public getIceConnectionState(): RTCIceConnectionState | null {
		return this.peerConnection?.iceConnectionState ?? null;
	}

	public getSignalingState(): RTCSignalingState | null {
		return this.peerConnection?.signalingState ?? null;
	}

	public isCurrentlyPublishing(): boolean {
		return this.isPublishing;
	}

	public getCurrentStreamInfo(): {
		streamId: string | null;
		deviceId: string | null;
		isPublishing: boolean;
		publishStartTime: number;
		hasVideo: boolean;
		hasAudio: boolean;
	} {
		return {
			streamId: this.currentStreamId,
			deviceId: this.currentDeviceId,
			isPublishing: this.isPublishing,
			publishStartTime: this.publishStartTime,
			hasVideo: (this.localStream?.getVideoTracks().length ?? 0) > 0,
			hasAudio: (this.localStream?.getAudioTracks().length ?? 0) > 0,
		};
	}

	public setAutoRepublishEnabled(enabled: boolean): void {
		if (!this.currentConfig) return;
		this.currentConfig.autoRepublishEnabled = enabled;

		if (enabled) {
			if (!this.autoRepublishIntervalJob) this.startAutoRepublishMonitoring();
		} else if (this.autoRepublishIntervalJob) {
			clearInterval(this.autoRepublishIntervalJob);
			this.autoRepublishIntervalJob = null;
		}
	}

	public async getConnectionStats(): Promise<RTCStatsReport | null> {
		if (!this.peerConnection) return null;
		try {
			return await this.peerConnection.getStats();
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ webrtc: `Error getting connection stats: ${error?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return null;
		}
	}

	/** Track de vídeo outbound activo en el sender (no solo WHIP OK). */
	public hasLiveOutboundVideo(): boolean {
		const track = this.getVideoSender()?.track;
		return track?.readyState === 'live';
	}

	/**
	 * Espera a que el vídeo outbound envíe bytes (RTP) tras CONNECTED/WHIP.
	 * Rechaza si no hay track live o bytesSent no crece en el timeout.
	 */
	public async waitForOutboundMedia(
		timeoutMs: number = OUTBOUND_MEDIA_DEFAULT_TIMEOUT_MS,
	): Promise<void> {
		const deadline = Date.now() + timeoutMs;
		let lastBytes: number | null = null;

		while (Date.now() < deadline) {
			if (!this.hasLiveOutboundVideo()) {
				await this.delay(OUTBOUND_MEDIA_POLL_MS);
				continue;
			}

			const bytes = await this.getOutboundVideoBytesSent();
			if (bytes != null) {
				if (lastBytes != null && bytes > lastBytes) {
					return;
				}
				lastBytes = bytes;
			}

			await this.delay(OUTBOUND_MEDIA_POLL_MS);
		}

		this.cloudLogsService.sendCloudLogs(
			{
				webrtc: 'waitForOutboundMedia timeout: no outbound video byte growth detected',
				hasLiveVideo: this.hasLiveOutboundVideo(),
				iceConnectionState: this.getIceConnectionState(),
			},
			ProcessLog.IN_PROCESS,
			LogType.Warning,
		);
		throw new Error(`Outbound video media not ready within ${timeoutMs}ms`);
	}

	private async getOutboundVideoBytesSent(): Promise<number | null> {
		const stats = await this.getConnectionStats();
		if (!stats) return null;

		let total = 0;
		let found = false;
		stats.forEach((report) => {
			if (report.type !== 'outbound-rtp') return;
			const outbound = report as RTCOutboundRtpStreamStats;
			const kind = outbound.kind ?? (outbound as { mediaType?: string }).mediaType;
			if (kind === 'video' && typeof outbound.bytesSent === 'number') {
				total += outbound.bytesSent;
				found = true;
			}
		});
		return found ? total : null;
	}

	private delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
}
